/**
 * Where the application's pages live.
 *
 * Builder-first: the report workspace is what opens at the root, because it is
 * what this application is for. Everything else hangs off it.
 *
 * Views are loaded lazily. The workspace pulls in a rich text editor, a
 * spreadsheet reader and a charting library, none of which a person landing on
 * the sign-in callback has any use for.
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

const routes: readonly RouteRecordRaw[] = [
  {
    path: '/',
    name: 'workspace',
    component: async () => import('@/views/WorkspaceView.vue'),
    meta: { requiresSignIn: true },
  },
  {
    path: '/reports/:id',
    name: 'report',
    component: async () => import('@/views/BuilderView.vue'),
    props: true,
    meta: { requiresSignIn: true },
  },
  {
    path: '/reports/:id/document',
    name: 'document-settings',
    component: async () => import('@/views/DocumentSettingsView.vue'),
    props: true,
    meta: { requiresSignIn: true },
  },
  {
    path: '/reports/:id/preview',
    name: 'preview',
    component: async () => import('@/views/PreviewView.vue'),
    props: true,
    meta: { requiresSignIn: true },
  },

  {
    path: '/access',
    name: 'access',
    component: async () => import('@/views/AccessView.vue'),
    meta: { requiresSignIn: true },
  },

  // The two the identity provider redirects to. Neither requires a session,
  // since establishing one is precisely what they are for.
  {
    path: '/callback',
    name: 'sign-in-callback',
    component: async () => import('@/views/SignInCallbackView.vue'),
  },
  {
    path: '/silentrenew',
    name: 'silent-renew',
    component: async () => import('@/views/SilentRenewView.vue'),
  },

  // The UI kit, every element on one page: for whoever works on the look,
  // so only in a development build.
  ...(import.meta.env.DEV
    ? [
        {
          path: '/kit',
          name: 'kit',
          component: async () => import('@/views/KitView.vue'),
          meta: { requiresSignIn: true },
        } satisfies RouteRecordRaw,
      ]
    : []),

  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: async () => import('@/views/NotFoundView.vue'),
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes: [...routes],
})
